import { useState } from 'react';
import { 
  PencilIcon, 
  TrashIcon, 
  EyeIcon,
  BellAlertIcon,
  MapPinIcon,
  ClockIcon,
  ExclamationTriangleIcon
} from '@heroicons/react/24/outline';
import AlertDetailsModal from './AlertDetailsModal';

export default function AlertTable({ 
  alertsData = [], 
  loading = false,
  onEdit,
  onDelete,
  onToggle,
  currentPage = 1,
  lastPage = 1,
  totalCount = 0,
  onPageChange,
  showEdit = true,
  showDelete = true,
  showCreate = true
}) {
  const [searchTerm, setSearchTerm] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [selectedAlert, setSelectedAlert] = useState(null);

  const filtered = alertsData?.filter(
    (alert) =>
      alert.details?.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      alert.details?.body?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      alert.place?.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      alert.details?.cat?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const handleEdit = (alert) => {
    if (onEdit) onEdit(alert);
  };

  const handleDelete = (alert) => {
    if (onDelete) onDelete(alert.id);
  };

  const handleToggle = (alert) => {
    if (onToggle) onToggle(alert.id, !alert.active);
  };

  const handleViewClick = (alert) => {
    setSelectedAlert(alert);
    setShowModal(true);
  };

  const getSeverityColor = (priority) => {
    if (priority > 80) return 'text-red-400 bg-red-900/20 border-red-500';
    if (priority > 50) return 'text-orange-400 bg-orange-900/20 border-orange-500';
    return 'text-yellow-400 bg-yellow-900/20 border-yellow-500';
  };

  const getSeverityText = (priority) => {
    if (priority > 80) return 'High';
    if (priority > 50) return 'Medium';
    return 'Low';
  };

  const formatDate = (timestamp) => {
    return new Date(timestamp * 1000).toLocaleString();
  };

  return (
    <div className="min-h-screen p-2 sm:p-4 py-4 sm:py-10">
      {/* Search Bar */}
      {/* <div className="mb-6">
        <div className="relative w-full">
          <input
            type="text"
            placeholder="Search alerts..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full h-14 pl-4 pr-4 rounded-full border border-gray-300 bg-[#384355] text-white placeholder-gray-300 focus:outline-none focus:ring-1 focus:ring-blue-500"
          />
        </div>
      </div> */}

      {/* Loader */}
      {loading ? (
        <div className="space-y-4">
          {[...Array(5)].map((_, i) => (
            <div key={i} className="animate-pulse bg-white border rounded-md p-4">
              <div className="h-4 bg-gray-200 rounded w-1/2 mb-2"></div>
              <div className="h-4 bg-gray-200 rounded w-full"></div>
            </div>
          ))}
        </div>
      ) : (
        <div className="rounded-md">
          {alertsData?.length === 0 ? (
            <div className="text-center text-gray-300 text-sm bg-transparent border-2 border-borderGray rounded-md py-12">
              <BellAlertIcon className="h-12 w-12 text-gray-400 mx-auto mb-4" />
              <p className="mb-4 text-lg font-semibold text-white">
                No Weather Alerts
              </p>
              <p className="mb-6 text-sm text-gray-300">
              You don't have any active weather alerts at the moment.
              Check back later for updates!
              </p>
            </div>
          ) : filtered.length === 0 ? (
            <div className="text-center text-gray-300 text-sm bg-transparent border-2 border-borderGray rounded-md py-12">
              <p className="mb-4 text-lg font-semibold text-white">
                No matching results found.
              </p>
              <p className="mb-6 text-sm text-gray-300">
                Try adjusting your search keywords.
              </p>
            </div>
          ) : (
            <>
              {/* Desktop View */}
              <div className="hidden md:block overflow-x-auto">
                <table className="min-w-full text-sm border border-gray-300 bg-transparent rounded-md shadow-sm">
                  <thead>
                    <tr className="bg-[#384355] text-base font-medium text-left text-white">
                      <th className="py-5 px-3 border-2 border-borderGray">Alert Name</th>
                      <th className="py-5 px-3 border-2 border-borderGray">Description</th>
                      <th className="py-5 px-3 border-2 border-borderGray">Location</th>
                      <th className="py-5 px-3 border-2 border-borderGray">Type</th>
                      <th className="py-5 px-3 border-2 border-borderGray">Severity</th>
                      <th className="py-5 px-3 border-2 border-borderGray">Status</th>
                      <th className="py-5 px-3 border-2 border-borderGray text-center">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filtered.map((alert, idx) => (
                      <tr key={alert.id || idx} className="bg-transparent hover:bg-gray-50/10">
                        <td className="py-10 px-3 border-2 border-borderGray font-medium text-white">
                          {alert.details?.name || '—'}
                        </td>
                        <td className="py-10 px-3 border-2 border-borderGray text-gray-300 max-w-[200px] truncate">
                          {alert.details?.body || '—'}
                        </td>
                        <td className="py-10 px-3 border-2 border-borderGray text-white">
                          {alert.place?.name || '—'}
                        </td>
                        <td className="py-10 px-3 border-2 border-borderGray">
                          <span className="px-2 py-1 bg-primaryBlue text-white rounded-full text-xs">
                            {alert.details?.cat || '—'}
                          </span>
                        </td>
                        <td className="py-10 px-3 border-2 border-borderGray">
                          <span className={`px-2 py-1 rounded-full text-xs font-medium border ${getSeverityColor(alert.details?.priority || 0)}`}>
                            {getSeverityText(alert.details?.priority || 0)}
                          </span>
                        </td>
                        <td className="py-10 px-3 border-2 border-borderGray">
                          <span
                            className={`text-xs font-semibold px-2 py-1 rounded ${
                              alert.active
                                ? 'bg-green-100 text-green-700'
                                : 'bg-red-100 text-red-700'
                            }`}
                          >
                            {alert.active ? 'Active' : 'Inactive'}
                          </span>
                        </td>
                        <td className="py-10 px-3 border-2 border-borderGray text-center">
                          <div className="flex justify-center space-x-2">
                            <button
                              onClick={() => handleViewClick(alert)}
                              className="bg-primaryBlue text-white px-3 py-1 rounded hover:bg-primaryBlue/80 text-xs font-medium"
                            >
                              View
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Mobile Card View */}
              <div className="md:hidden space-y-4">
                {filtered.map((alert, idx) => (
                  <div key={alert.id || idx} className="bg-[#384355] p-4 rounded-lg border border-borderGray shadow-sm">
                    <div className="flex justify-between items-start mb-3">
                      <div className="flex-1 mr-2">
                        <h3 className="text-white font-semibold text-base mb-1">{alert.details?.name || '—'}</h3>
                        <p className="text-xs text-gray-400 flex items-center">
                          <MapPinIcon className="h-3 w-3 mr-1" />
                          {alert.place?.name || '—'}
                        </p>
                      </div>
                      <span
                        className={`text-xs font-semibold px-2 py-1 rounded whitespace-nowrap ${
                          alert.active
                            ? 'bg-green-100 text-green-700'
                            : 'bg-red-100 text-red-700'
                        }`}
                      >
                        {alert.active ? 'Active' : 'Inactive'}
                      </span>
                    </div>
                    
                    <p className="text-gray-300 text-sm mb-4 line-clamp-2">
                      {alert.details?.body || '—'}
                    </p>

                    <div className="flex flex-wrap items-center justify-between gap-2 pt-3 border-t border-gray-600">
                      <div className="flex items-center space-x-2">
                        <span className="px-2 py-1 bg-primaryBlue text-white rounded-full text-xs">
                          {alert.details?.cat || '—'}
                        </span>
                        <span className={`px-2 py-1 rounded-full text-xs font-medium border ${getSeverityColor(alert.details?.priority || 0)}`}>
                          {getSeverityText(alert.details?.priority || 0)}
                        </span>
                      </div>
                      
                      <button
                        onClick={() => handleViewClick(alert)}
                        className="bg-primaryBlue text-white px-4 py-1.5 rounded hover:bg-primaryBlue/80 text-xs font-medium"
                      >
                        View Details
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </>
          )}
        </div>
      )}

      {/* Pagination */}
      {totalCount > 0 && lastPage > 1 && (
        <div className="mt-6 flex justify-center items-center space-x-2">
          <button
            onClick={() => onPageChange(currentPage - 1)}
            disabled={currentPage === 1}
            className="px-3 py-2 text-sm border-2 border-borderGray bg-[#384355] text-white rounded-md disabled:opacity-50 disabled:cursor-not-allowed hover:bg-[#4a5568]"
          >
            Previous
          </button>
          <span className="text-sm text-white">
            Page {currentPage} of {lastPage}
          </span>
          <button
            onClick={() => onPageChange(currentPage + 1)}
            disabled={currentPage === lastPage}
            className="px-3 py-2 text-sm border-2 border-borderGray bg-[#384355] text-white rounded-md disabled:opacity-50 disabled:cursor-not-allowed hover:bg-[#4a5568]"
          >
            Next
          </button>
        </div>
      )}

      {/* Alert Details Modal */}
      <AlertDetailsModal
        isOpen={showModal}
        onClose={() => setShowModal(false)}
        alert={selectedAlert}
      />
    </div>
  );
}